<script setup lang="ts">
/**
 * One ticket on a kitchen screen.
 *
 * READ AT TWO METRES, in a room that is loud, bright and hot, by somebody
 * holding a pan. That constraint decides everything here: the table number is
 * the largest thing on the card because it is what a cook calls out, the items
 * are next, and every control is a whole row rather than an icon.
 *
 * AGE IS NEVER COLOUR ALONE. A LATE ticket carries the word as well as the
 * border, because a colour-blind cook is still a cook and a greyscale
 * screenshot still goes in a ticket to support (K10).
 *
 * THE ALLERGEN LINE IS THE POINT OF THIS WHOLE APPLICATION. It is not a badge
 * in a corner: it is a full-width band that has to be acknowledged before the
 * ticket will leave the board, and until it is, the bump button says so.
 *
 * ONE BAND PER TICKET, not one per line (2026-09-25). Every dish on the Czech
 * menu declares something — the beer declares gluten — so a band per line
 * turned the pass into a wall of salmon with a "Potvrdit" under every dish,
 * three of them on a six-top, and a band that is everywhere is a band nobody
 * reads. The SAFETY is unchanged: the bump stays blocked until every
 * allergen-carrying line is acknowledged, and each acknowledgement is still
 * its own attributed event per line — the one "Potvrdit" emits one per line
 * still waiting. What changed is that the cook reads the list once, at the
 * top of the ticket, and each line carries its own allergens as small tags so
 * the plate they belong to is never in doubt.
 */
import { computed } from "vue";
import type { Translator } from "@gf/kernel/i18n";
import type { BoardTicket } from "../station";

const props = defineProps<{
  ticket: BoardTicket;
  t: Translator;
  /** Lines still needing somebody to take responsibility. */
  unacknowledged: readonly string[];
  /** Per-dish Start / Hotovo. Off, the whole ticket is the bump target. */
  detailed?: boolean;
}>();

const emit = defineEmits<{
  acknowledge: [selectionId: string];
  start: [selectionId: string];
  finish: [selectionId: string];
  bump: [];
}>();

const blocked = computed(() => props.unacknowledged.length > 0);

/** A tap on the ticket sends it — unless per-dish tracking is on, or an allergen waits. */
const tapped = (): void => {
  if (!props.detailed && !blocked.value) emit("bump");
};
const pending = new Set<string>();

const needsAck = (selectionId: string): boolean => props.unacknowledged.includes(selectionId);

/** Lines that are still going to be cooked, with something declared on them. */
const declaring = computed(() => props.ticket.items.filter((i) => !i.voided && i.allergens.length > 0));

/** Every allergen on the ticket, once each, in the order the dishes declare them. */
const allergenNames = computed(() => {
  const seen = new Set<string>();
  for (const item of declaring.value) for (const code of item.allergens) seen.add(code);
  return [...seen].map((code) => props.t.t(`allergen.${code}`));
});

/** The lines on THIS ticket still waiting for somebody to read them. */
const waitingAck = computed(() => declaring.value.filter((i) => needsAck(i.selectionId)).map((i) => i.selectionId));

/** One press, one attributed acknowledgement per line still waiting. */
const acknowledgeAll = (): void => {
  for (const selectionId of waitingAck.value) emit("acknowledge", selectionId);
};

/** Minutes since it was fired, for the corner of the card. */
const waited = computed(() => {
  const ms = Date.now() - new Date(props.ticket.firedAt).getTime();
  return Math.max(0, Math.floor(ms / 60_000));
});

const quantity = (milli: number): string => (milli === 1_000 ? "" : `${milli / 1_000}× `);

/** "4", "Zahrádka 2", "Účet 3", "A07" — never "T4" or "pickup:A07". */
const tableLabel = computed(() => {
  const ref = props.ticket.tableRef;
  if (props.ticket.pickupCode) return props.ticket.pickupCode;
  if (props.ticket.tableName) return props.ticket.tableName;
  const tab = /^tab:(\d+)$/.exec(ref);
  return tab ? props.t.t("kds.tab", { n: tab[1]! }) : ref;
});

/** Every dish voided: nothing to cook, and the ticket can be cleared. */
const cancelled = computed(() => props.ticket.items.length > 0 && props.ticket.items.every((i) => i.voided));
void pending;
</script>

<template>
  <article
    class="ticket"
    :class="[`ticket--${ticket.age.toLowerCase()}`, { 'ticket--tap': !detailed && !blocked }]"
    :data-ticket="ticket.ticketId"
    @click="tapped"
  >
    <header class="ticket__head">
      <span class="ticket__table" :class="{ 'ticket__table--long': tableLabel.length > 4 }">{{ tableLabel }}</span>
      <span v-if="ticket.courseId" class="ticket__course" data-testid="ticket-course">{{ t.t("kds.course", { course: ticket.courseId }) }}</span>
      <span class="ticket__meta">
        <!-- The word, not only the border. -->
        <span class="ticket__age">{{ t.t(`kds.age.${ticket.age}`) }}</span>
        <span class="ticket__waited">{{ t.t("kds.waited", { minutes: waited }) }}</span>
      </span>
    </header>

    <p v-if="ticket.server" class="ticket__server">{{ ticket.server }}</p>
    <p v-if="cancelled" class="ticket__cancelled" role="status">{{ t.t("kds.cancelled") }}</p>
    <p v-if="ticket.recallCount > 0" class="ticket__recalled" role="status">
      {{ t.t("kds.recalled", { count: ticket.recallCount }) }}
    </p>

    <!-- FULL WIDTH, not a badge, and ONCE per ticket: a cook scanning a board
         at arm's length must not have to look for this, nor read it five times. -->
    <div v-if="allergenNames.length > 0" class="allergens" :class="{ 'allergens--open': waitingAck.length > 0 }">
      <span class="allergens__codes">
        <span class="allergens__label">⚠ {{ waitingAck.length > 0 ? t.t("kds.allergens") : t.t("kds.allergensAcknowledged") }}:</span>
        {{ allergenNames.join(" · ") }}
      </span>
      <button v-if="waitingAck.length > 0" type="button" class="allergens__ack" @click.stop="acknowledgeAll">
        {{ t.t("kds.acknowledge") }}
      </button>
    </div>

    <ul class="ticket__items">
      <li v-for="item in ticket.items" :key="item.selectionId" class="line" :class="{ 'line--voided': item.voided }">
        <div class="line__head">
          <span class="line__name" :class="{ 'line__name--done': item.state === 'DONE' }">
            {{ quantity(item.quantityMilli) }}{{ item.name }}
          </span>
          <span v-if="item.voided" class="line__void">{{ t.t("kds.voided") }}</span>
          <span v-else class="line__state">{{ t.t(`kds.item.${item.state}`) }}</span>
        </div>
        <ul v-if="item.modifiers && item.modifiers.length > 0" class="line__modifiers">
          <li v-for="(m, i) in item.modifiers" :key="i">
            + {{ m.quantity > 1 ? `${m.quantity}× ` : "" }}{{ m.name }}
          </li>
        </ul>
        <p v-if="item.note" class="line__note">{{ item.note }}</p>
        <!-- Which plate the ticket's allergens belong to: small, and on the line. -->
        <ul
          v-if="item.allergens.length > 0 && !item.voided"
          class="line__allergens"
          :class="{ 'line__allergens--open': needsAck(item.selectionId) }"
        >
          <li v-for="code in item.allergens" :key="code" class="line__allergen">{{ t.t(`allergen.${code}`) }}</li>
        </ul>

        <div v-if="detailed && !item.voided" class="line__actions">
          <button
            type="button"
            class="line__button"
            :disabled="item.state !== 'PENDING'"
            @click="emit('start', item.selectionId)"
          >
            {{ t.t("kds.start") }}
          </button>
          <button
            type="button"
            class="line__button"
            :disabled="item.state === 'DONE'"
            @click="emit('finish', item.selectionId)"
          >
            {{ t.t("kds.finish") }}
          </button>
        </div>
      </li>
    </ul>

    <!-- Disabled AND explained. A control that refuses without saying why
         teaches a kitchen that the screen is broken. -->
    <button type="button" class="ticket__bump" :disabled="blocked" @click.stop="emit('bump')">
      {{ blocked ? t.t("kds.bump.blocked") : t.t("kds.bump") }}
    </button>
  </article>
</template>

<style scoped>
.ticket__course {
  align-self: center;
  padding: 2px var(--gf-space-2);
  border-radius: var(--gf-radius-sm);
  background: var(--gf-color-surface-active);
  color: var(--gf-color-text);
  font-weight: var(--gf-weight-semibold);
}

/*
  A ticket is read from two metres away by somebody who has just put a pan
  down. Everything here is about making ONE of them findable in a wall of
  them — which means hierarchy, not decoration.

  THE LOOK IS MYMONDAY'S (2026-09-25): a calm card with a hairline edge and no
  shadow, because a board of twenty tickets each casting a shadow is twenty
  things shouting equally. Urgency is carried by the HEADER — nothing, a
  warning band, then a solid band — so the eye finds the late one first.
*/
.ticket {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-2);
  /* Room for the urgency bar along the top edge. */
  padding: var(--gf-space-3);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-surface-raised);
  color: var(--gf-color-text);
  overflow: hidden;
  position: relative;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

/*
  URGENCY IN THE HEADER, in addition to the WORD.

  A 3 px edge at two metres in a room with steam in it is not a signal — it is
  a detail you find once you are already looking at the card. So the header
  itself changes: a thin success bar while the ticket is on time, a tinted
  warning band when it needs watching, and a SOLID danger band when it is late —
  three steps that differ in LIGHTNESS as well as hue, visible in peripheral
  vision, which is the only way a late ticket gets noticed before somebody
  complains about it.

  It is never the only signal: `ticket__age` says the word, which is what
  SC 1.4.1 requires and what a colour-blind cook needs.
*/
.ticket::before {
  content: "";
  position: absolute;
  inset: 0 0 auto;
  height: var(--gf-space-0);
  background: var(--gf-color-success);
}

/* The whole card is the target: a knuckle, not a fingertip. */
.ticket--tap {
  cursor: pointer;
  user-select: none;
}

.ticket--tap:active {
  background: var(--gf-color-surface-hover);
}

.ticket--caution::before,
.ticket--late::before {
  content: none;
}

.ticket__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gf-space-2);
  /* Full-bleed, so a tinted or solid band reaches the card's edges. */
  margin: calc(-1 * var(--gf-space-3)) calc(-1 * var(--gf-space-3)) 0;
  padding: var(--gf-space-3) var(--gf-space-3) var(--gf-space-2);
}

.ticket--caution .ticket__head {
  background: var(--gf-color-warning-surface);
}

.ticket--late .ticket__head {
  background: var(--gf-color-danger);
  color: var(--gf-color-on-danger);
}

.ticket__table {
  min-width: 0;
  font-size: var(--gf-text-3xl);
  font-weight: var(--gf-weight-bold);
  line-height: 1;
  letter-spacing: var(--gf-tracking-tight);
  font-variant-numeric: tabular-nums;
}

/* "Zahrádka 2" is still the largest thing on the card, one step down so the
   age beside it is not squeezed off the edge. */
.ticket__table--long {
  font-size: var(--gf-text-2xl);
}

.ticket__meta {
  flex: none;
  margin-left: auto;
  white-space: nowrap;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--gf-space-0);
}

/* The age is the second thing read, after the table. Sentence case, bold:
   weight carries it, not capitals. */
.ticket__age {
  font-size: var(--gf-text-md);
  font-weight: var(--gf-weight-bold);
  color: var(--gf-color-success);
}

.ticket--caution .ticket__age {
  color: var(--gf-color-warning);
}

.ticket--late .ticket__age {
  color: inherit;
}

.ticket__waited {
  font-size: var(--gf-text-lg);
  font-weight: var(--gf-weight-semibold);
  color: var(--gf-color-text-muted);
  font-variant-numeric: tabular-nums;
}

.ticket--late .ticket__waited {
  color: inherit;
}

.ticket__recalled {
  margin: 0;
  padding: var(--gf-space-1) var(--gf-space-2);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-warning-surface);
  color: var(--gf-color-warning);
  font-weight: var(--gf-weight-bold);
}

/*
  DENSER THAN IT WAS (2026-09-25): at 1280×800 only two or three dishes of a
  six-top fitted above the fold. The lines now sit a hairline apart with a
  little air either side, the dish name one step down from `lg` — 24 px bold
  still reads across a pass — and the board runs four columns at 1280.
*/
.ticket__items {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.line {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-0);
  padding: var(--gf-space-1) 0;
  border-top: var(--gf-border-thin) solid var(--gf-color-border-subtle);
}

.line__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gf-space-2);
}

.line__name {
  font-size: var(--gf-text-md);
  font-weight: var(--gf-weight-bold);
  line-height: var(--gf-leading-tight);
}

.line__name--done {
  text-decoration: line-through;
  color: var(--gf-color-text-muted);
}

.line__state {
  color: var(--gf-color-text-muted);
}

/* Voided after it was fired: struck, labelled, and nothing to press. */
.line--voided .line__name {
  text-decoration: line-through;
  color: var(--gf-color-text-muted);
}

/* A tinted pill, not a solid block: it is a fact about a line, and the solid
   danger fill is reserved for the two things that must stop a cook — a late
   ticket and an unread allergen. */
.line__void {
  padding: 0 var(--gf-space-2);
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-bold);
  color: var(--gf-color-danger);
  background: var(--gf-color-danger-surface);
  border-radius: var(--gf-radius-full);
}

.ticket__server {
  margin: 0;
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.ticket__cancelled {
  margin: 0;
  padding: var(--gf-space-1) var(--gf-space-2);
  font-weight: var(--gf-weight-bold);
  color: var(--gf-color-danger);
  background: var(--gf-color-danger-surface);
  border-radius: var(--gf-radius-md);
}

.line__modifiers {
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-semibold);
  color: var(--gf-color-text-muted);
}

/* The note is an instruction the plate depends on, so it is boxed rather than
   left to read as part of the dish's name — a tint, not a bar. */
.line__note {
  margin: 0;
  padding: var(--gf-space-0) var(--gf-space-2);
  font-size: var(--gf-text-base);
  font-weight: var(--gf-weight-semibold);
  background: var(--gf-color-warning-surface);
  color: var(--gf-color-warning);
  border-radius: var(--gf-radius-md);
}

/* Read: a quiet line that still names them, for whoever plates it. */
.allergens {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gf-space-2);
  padding: var(--gf-space-1) var(--gf-space-2);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-sunken);
  color: var(--gf-color-text-muted);
  font-weight: var(--gf-weight-semibold);
}

/* Unread: the loudest thing on the board, by design — once per ticket. The
   list takes the whole first row and "Potvrdit" wraps beneath it as a full-
   width bar: squeezed side by side in a 300 px card, the allergens stacked one
   word per line beside the button. */
.allergens--open {
  flex-wrap: wrap;
  padding: var(--gf-space-2);
  background: var(--gf-color-danger);
  color: var(--gf-color-on-danger);
  font-weight: var(--gf-weight-bold);
}

.allergens__codes {
  flex: 1 1 12rem;
}

.allergens--open .allergens__ack {
  flex: 1 1 auto;
}

.allergens__label {
  margin-right: var(--gf-space-1);
  font-weight: var(--gf-weight-bold);
}

/* On the line: which plate carries what. Tinted while unread, quiet after. */
.line__allergens {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gf-space-0) var(--gf-space-1);
  margin: 0;
  padding: 0;
  list-style: none;
}

.line__allergen {
  padding: 0 var(--gf-space-1);
  border-radius: var(--gf-radius-full);
  background: var(--gf-color-surface-sunken);
  color: var(--gf-color-text-muted);
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-semibold);
}

.line__allergens--open .line__allergen {
  background: var(--gf-color-danger-surface);
  color: var(--gf-color-danger);
}

.allergens__ack {
  min-height: var(--gf-target-min);
  padding: 0 var(--gf-space-3);
  border: var(--gf-border-thin) solid currentColor;
  border-radius: var(--gf-radius-md);
  background: color-mix(in srgb, currentColor 12%, transparent);
  color: inherit;
  font: inherit;
  font-weight: var(--gf-weight-bold);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.allergens__ack:active {
  background: color-mix(in srgb, currentColor 24%, transparent);
}

.line__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--gf-space-1);
}

.line__button {
  min-height: var(--gf-target-min);
  border: var(--gf-border-thin) solid var(--gf-color-border-subtle);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface);
  color: var(--gf-color-text);
  font: inherit;
  font-weight: var(--gf-weight-medium);
  box-shadow: var(--gf-shadow-sm);
  cursor: pointer;
  transition: background-color var(--gf-motion-fast) var(--gf-ease-out);
}

.line__button:active:not(:disabled) {
  background: var(--gf-color-surface-active);
  box-shadow: none;
}

/*
  The bump bar. ~83 px tall at kiosk density, hit by a hand that has just put
  down a pan — and no longer the only target: the whole ticket bumps on a tap,
  so the bar gave up a little height to the dishes (2026-09-25).

  NEUTRAL, not a fill (2026-09-25). It was `primary`, which in the dark kitchen
  theme was a near-white slab on every ticket — twenty of the brightest things
  on the board, drowning out the late band and the allergen band, which are the
  two things a kitchen screen exists to make seen. It stays disabled AND
  EXPLAINED when the allergens are unacknowledged: the label itself changes
  rather than the button merely greying out.
*/
.ticket__bump {
  min-height: calc(var(--gf-target-min) * 1.15);
  border: var(--gf-border-thin) solid var(--gf-color-border);
  border-radius: var(--gf-radius-md);
  background: var(--gf-color-surface-active);
  color: var(--gf-color-text);
  font: inherit;
  font-size: var(--gf-text-lg);
  font-weight: var(--gf-weight-bold);
  cursor: pointer;
  transition:
    background-color var(--gf-motion-fast) var(--gf-ease-out),
    box-shadow var(--gf-motion-fast) var(--gf-ease-out);
}

/*
  A PRESS THE COOK CAN SEE, and this one earns its place more than any other in
  the product: the bump is debounced for 500 ms, so a second press inside the
  window is swallowed and returns `ok`. Without feedback that is a control that
  sometimes appears to do nothing, which is how a ticket gets bumped twice as
  soon as the debounce lifts. A firm ring and a changed fill — no movement.
*/
.ticket__bump:active:not(:disabled) {
  background: var(--gf-color-surface-hover);
  box-shadow: inset 0 0 0 var(--gf-border-thick) var(--gf-color-border-strong);
}

.ticket__bump:disabled,
.line__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.ticket__bump:focus-visible,
.line__button:focus-visible,
.allergens__ack:focus-visible {
  outline: var(--gf-focus-width) solid var(--gf-color-focus);
  outline-offset: var(--gf-focus-offset);
}

@media (prefers-reduced-motion: reduce) {
  .ticket,
  .ticket__bump,
  .line__button,
  .allergens__ack {
    transition: none;
  }
}
</style>
